import { useRef } from 'react';
import Image from 'next/image';
import cn from 'classnames';

import Heading from '@components/ui/Heading';
import LinkButton from '_components/UI/LinkButton';
import HeroInfoCard from '_components/HeroInfoCard';
import useObjectVisibility from '@hooks/useObjectVisibility';
import useBreakpoint, { Breakpoints } from '@hooks/useBreakpoint';
import { useLocale } from '@hooks/useLocale';
import { localizeHref } from '@utils/localizeHref';
import {
  INFO_CARD_CTA_SLOT,
  heroCtaHandlers,
} from '_utils/GA4/heroCtaHandlers';
import { MainHeroData } from './types';

import styles from './MainHero.module.scss';

export interface MainHeroProps {
  data: MainHeroData;
  size?: 'default' | 'large';
  trackingType?: string;
}

const MainHero = ({ data, size = 'default', trackingType }: MainHeroProps) => {
  const {
    h1,
    subCopy,
    image,
    mobileImage,
    ctas,
    infoCard,
    animation,
    coOpBrand,
  } = data;
  const sectionRef = useRef<HTMLElement | null>(null);
  const revealed = useObjectVisibility(sectionRef);
  const isMobile = useBreakpoint(Breakpoints.md);
  const locale = useLocale();

  const desktopImageUrl = image?.url ?? mobileImage?.url ?? null;
  const mobileImageUrl = mobileImage?.url ?? image?.url ?? null;
  const hasDistinctImages =
    !!desktopImageUrl && !!mobileImageUrl && desktopImageUrl !== mobileImageUrl;
  const infoCardHref = infoCard ? localizeHref(infoCard.cta.url, locale) : null;

  const tracking = { type: trackingType, coOpBrand };

  return (
    <section
      ref={sectionRef}
      className={cn(
        styles.hero,
        size === 'large' && styles.large,
        revealed && styles.revealed
      )}
      data-animation={animation}
      data-testid="main-hero"
    >
      {desktopImageUrl && (
        <>
          <div className={styles.background}>
            <Image
              priority
              src={desktopImageUrl}
              alt={image?.alt ?? mobileImage?.alt ?? ''}
              fill
              sizes="100vw"
              className={cn(
                styles.backgroundImage,
                hasDistinctImages && styles.desktopImage
              )}
            />
            {hasDistinctImages && (
              <Image
                priority
                src={mobileImageUrl as string}
                alt={mobileImage?.alt ?? image?.alt ?? ''}
                fill
                sizes="100vw"
                className={cn(styles.backgroundImage, styles.mobileImage)}
              />
            )}
          </div>
          <div className={styles.overlay} aria-hidden="true" />
        </>
      )}
      <div className={styles.content}>
        {(h1.trim() || subCopy) && (
          <div className={styles.headingGroup}>
            {h1.trim() && (
              <Heading
                semanticLevel="h1"
                styleLevel="1"
                className={styles.title}
              >
                {h1}
              </Heading>
            )}
            {subCopy && <p className={styles.subCopy}>{subCopy}</p>}
          </div>
        )}
        {ctas.length > 0 && (
          <div className={styles.ctaRow}>
            {ctas.map(({ label, url, slot }) => {
              const href = localizeHref(url, locale);
              return (
                <LinkButton
                  key={`${url}-${slot}`}
                  href={href}
                  variant="white"
                  {...heroCtaHandlers(tracking, href, label, slot)}
                >
                  {label}
                </LinkButton>
              );
            })}
          </div>
        )}
      </div>
      {infoCard && !isMobile && (
        <HeroInfoCard
          {...infoCard}
          href={infoCardHref as string}
          className={styles.infoCard}
          {...heroCtaHandlers(
            tracking,
            infoCardHref as string,
            infoCard.cta.label,
            INFO_CARD_CTA_SLOT
          )}
        />
      )}
    </section>
  );
};

export default MainHero;
